import React, { useEffect, useState } from 'react';
import {
  Calendar, Clock, Plus, User, MapPin,
  ChevronLeft, ChevronRight, Edit3, X, Check, AlertTriangle,
  Video, RefreshCw, Bell, ExternalLink,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import PatientDropdown from '../components/PatientDropdown';
import { NEU } from '../neuStyles';
import { writeParams } from '../lib/urlState';

const STATUS_COLORS = {
  scheduled:   { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  confirmed:   { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  checked_in:  { bg: '#cffafe', text: '#155e75', border: '#67e8f9' },
  in_progress: { bg: '#fef9c3', text: '#854d0e', border: '#fde047' },
  completed:   { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
  cancelled:   { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  no_show:     { bg: '#7f1d1d', text: '#ffffff', border: '#450a0a' },
};

const PRIORITY_LABELS = { routine: 'Routine', urgent: 'Urgent', emergent: 'Emergent' };
const PRIORITY_COLORS = {
  routine:  { bg: '#f0fdf4', text: '#166534' },
  urgent:   { bg: '#fff7ed', text: '#9a3412' },
  emergent: { bg: '#fef2f2', text: '#7f1d1d' },
};

const APPOINTMENT_TYPES = [
  { value: 'office_visit',   label: 'Office Visit' },
  { value: 'telehealth',     label: 'Telehealth' },
  { value: 'follow_up',      label: 'Follow-up' },
  { value: 'procedure',      label: 'Procedure' },
  { value: 'lab_work',       label: 'Lab Work' },
  { value: 'physical_exam',  label: 'Physical Exam' },
  { value: 'consultation',   label: 'Consultation' },
  { value: 'urgent_care',    label: 'Urgent Care' },
];

const RECURRENCE_OPTIONS = [
  { value: '',          label: 'None (one-time)' },
  { value: 'weekly',    label: 'Weekly' },
  { value: 'biweekly',  label: 'Every 2 weeks' },
  { value: 'monthly',   label: 'Monthly' },
];

const EMPTY_FORM = {
  patientId:           '',
  providerId:          '',
  appointmentType:     '',
  appointmentDate:     '',
  startTime:           '',
  endTime:             '',
  durationMinutes:     '30',
  priority:            'routine',
  chiefComplaint:      '',
  reason:              '',
  room:                '',
  telehealthLink:      '',
  referralSource:      '',
  insuranceAuthNumber: '',
  copayAmount:         '',
  recurrence:          '',
  reminderSent:        false,
  notes:               '',
};

// ─── Shared modal styles ────────────────────────────────────────────────────
const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999, padding: '1rem',
  backdropFilter: 'blur(3px)',
};
const modalStyle = {
  ...NEU.modal,
  maxWidth: '760px', width: '100%',
  maxHeight: '90vh', overflowY: 'auto',
  padding: 0,
};

// ─── Reusable NEU nav-arrow button style ────────────────────────────────────
const navBtnStyle = {
  ...NEU.btnGhost,
  width: 36, height: 36,
  borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 0, flexShrink: 0,
};

// ─── Section header used inside modals ─────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{
      fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
      letterSpacing: '0.08em', color: '#2E8B98',
      marginBottom: '0.6rem', marginTop: '0.25rem',
      paddingBottom: '0.4rem',
      boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.5)',
    }}>
      {children}
    </div>
  );
}

// ─── NEU input wrapper (replaces className="form-input") ───────────────────
const neuInputStyle = {
  ...NEU.input,
  padding: '8px 12px',
  fontSize: '0.875rem',
  width: '100%',
  boxSizing: 'border-box',
  color: '#374151',
};
const neuInputErrStyle = {
  ...neuInputStyle,
  boxShadow: `inset 2px 2px 4px rgba(239,68,68,0.25), inset -2px -2px 4px rgba(255,255,255,0.8)`,
};

export default function SchedulingPage({ user, onNavigate }) {
  const [view, setView]                         = useState('month');
  const [selectedDate, setSelectedDate]         = useState(new Date().toISOString().split('T')[0]);
  const [monthData, setMonthData]               = useState({});
  const [appointments, setAppointments]         = useState([]);
  const [weekAppointments, setWeekAppointments] = useState({});
  const [loading, setLoading]                   = useState(false);
  const [showCreateForm, setShowCreateForm]     = useState(false);
  const [selectedAppt, setSelectedAppt]         = useState(null);
  const [showDetails, setShowDetails]           = useState(false);
  const [patients, setPatients]                 = useState([]);
  const [providers, setProviders]               = useState([]);
  const [formData, setFormData]                 = useState({ ...EMPTY_FORM, appointmentDate: new Date().toISOString().split('T')[0] });
  const [formErrors, setFormErrors]             = useState({});
  const [cancelReason, setCancelReason]         = useState('');
  const [showCancelForm, setShowCancelForm]     = useState(false);
  const [saving, setSaving]                     = useState(false);

  const canCreate = user?.permissions?.includes('scheduling:create');
  const canUpdate = user?.permissions?.includes('scheduling:update');
  const canDelete = user?.permissions?.includes('scheduling:delete');

  useEffect(() => { loadPatients(); loadProviders(); }, []);
  useEffect(() => { loadAppointments(); }, [selectedDate, view]);

  // ─── Data loaders ─────────────────────────────────────────────────────────

  async function loadAppointments() {
    setLoading(true);
    try {
      if (view === 'day') {
        const data = await window.zignagenixAPI.getAppointmentsByDate(selectedDate);
        setAppointments((data || []).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')));
      } else if (view === 'week') {
        const startDate = getWeekStart(new Date(selectedDate));
        const weekData  = {};
        for (let i = 0; i < 7; i++) {
          const d = new Date(startDate);
          d.setDate(d.getDate() + i);
          const ds = d.toISOString().split('T')[0];
          const day = await window.zignagenixAPI.getAppointmentsByDate(ds);
          weekData[ds] = (day || []).length;
        }
        setWeekAppointments(weekData);
      } else if (view === 'month') {
        const d = new Date(selectedDate + 'T00:00:00');
        const data = await window.zignagenixAPI.getAppointmentsByMonth(d.getFullYear(), d.getMonth() + 1);
        setMonthData(data || {});
      }
    } catch (err) {
      console.error('Error loading appointments:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadPatients() {
    try {
      const data = await window.zignagenixAPI.getPatients();
      setPatients(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Error loading patients:', err); }
  }

  async function loadProviders() {
    try {
      const data = await window.zignagenixAPI.getUsers();
      setProviders(Array.isArray(data) ? data.filter(u => u.role === 'physician') : []);
    } catch (err) { console.error('Error loading providers:', err); }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function getWeekStart(date) {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    return d;
  }

  function getWeekDates() {
    const start = getWeekStart(new Date(selectedDate));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d.toISOString().split('T')[0];
    });
  }

  function formatDate(dateStr) {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  }

  function formatTime(time) {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  }

  function navigateDay(dir)   { const d = new Date(selectedDate); d.setDate(d.getDate() + dir); setSelectedDate(d.toISOString().split('T')[0]); }
  function navigateWeek(dir)  { const d = new Date(selectedDate); d.setDate(d.getDate() + dir * 7); setSelectedDate(d.toISOString().split('T')[0]); }
  function navigateMonth(dir) { const d = new Date(selectedDate + 'T00:00:00'); d.setMonth(d.getMonth() + dir); setSelectedDate(d.toISOString().split('T')[0]); }
  function goToToday()        { setSelectedDate(new Date().toISOString().split('T')[0]); }

  function getMonthCalendarDays() {
    const d = new Date(selectedDate + 'T00:00:00');
    const year = d.getFullYear(), month = d.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      cells.push({ date: new Date(year, month - 1, day).toISOString().split('T')[0], day, currentMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push({ date: new Date(year, month, day).toISOString().split('T')[0], day, currentMonth: true });
    }
    for (let day = 1; cells.length < 42; day++) {
      cells.push({ date: new Date(year, month + 1, day).toISOString().split('T')[0], day, currentMonth: false });
    }
    return cells;
  }

  function getMonthLabel() {
    return new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  const maxDate = (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().split('T')[0]; })();
  const todayStr = new Date().toISOString().split('T')[0];

  function getPatientName(id) {
    const p = patients.find(x => x.id === id);
    if (!p) return 'Unknown Patient';
    return p.name || `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^, |, $/, '') || 'Unknown Patient';
  }

  function getProviderName(id) {
    const p = providers.find(x => x.id === id);
    return p ? (p.name || p.display_name || 'Unknown Provider') : 'Unknown Provider';
  }

  function getTypeLabel(val) {
    const t = APPOINTMENT_TYPES.find(x => x.value === val);
    return t ? t.label : val;
  }

  function updateField(field, value) {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if ((field === 'startTime' || field === 'durationMinutes') && next.startTime && next.durationMinutes) {
        const [h, m] = next.startTime.split(':').map(Number);
        const total = h * 60 + m + parseInt(next.durationMinutes || 30);
        next.endTime = `${String(Math.floor(total / 60) % 24).padStart(2,'0')}:${String(total % 60).padStart(2,'0')}`;
      }
      return next;
    });
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
  }

  function validateForm() {
    const errors = {};
    if (!formData.patientId)       errors.patientId       = 'Patient is required';
    if (!formData.providerId)      errors.providerId      = 'Provider is required';
    if (!formData.appointmentType) errors.appointmentType = 'Appointment type is required';
    if (!formData.appointmentDate) {
      errors.appointmentDate = 'Date is required';
    } else if (new Date(formData.appointmentDate) < new Date()) {
      errors.appointmentDate = 'Appointment date must be in the future';
    } else if (formData.appointmentDate > maxDate) {
      errors.appointmentDate = 'Cannot schedule more than 12 months ahead';
    }
    if (!formData.startTime)              errors.startTime      = 'Start time is required';
    if (!formData.endTime)                errors.endTime        = 'End time is required';
    if (!formData.chiefComplaint?.trim()) errors.chiefComplaint = 'Chief complaint is required';
    if (formData.appointmentType === 'telehealth' && !formData.telehealthLink?.trim())
      errors.telehealthLink = 'Telehealth link is required for telehealth appointments';
    if (formData.copayAmount && isNaN(parseFloat(formData.copayAmount)))
      errors.copayAmount = 'Copay must be a number';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  // ─── Form actions ─────────────────────────────────────────────────────────

  async function handleCreateSubmit(e) {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      const result = await window.zignagenixAPI.createAppointment(formData);
      if (result?.error) { alert(result.message || 'Failed to create appointment'); return; }
      setShowCreateForm(false);
      resetForm();
      loadAppointments();
    } catch (err) {
      console.error('Error creating appointment:', err);
      alert('Failed to create appointment');
    } finally {
      setSaving(false);
    }
  }

  function resetForm() { setFormData({ ...EMPTY_FORM, appointmentDate: selectedDate }); setFormErrors({}); }
  function openCreateForm() { resetForm(); setShowCreateForm(true); }

  async function handleStatusUpdate(status) {
    try {
      await window.zignagenixAPI.updateAppointmentStatus(selectedAppt.id, status);
      setShowDetails(false);
      setSelectedAppt(null);
      loadAppointments();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update appointment status');
    }
  }

  async function handleCancel() {
    if (!cancelReason.trim()) { alert('Please provide a cancellation reason'); return; }
    try {
      await window.zignagenixAPI.cancelAppointment(selectedAppt.id, cancelReason);
      setShowDetails(false);
      setShowCancelForm(false);
      setCancelReason('');
      setSelectedAppt(null);
      loadAppointments();
    } catch (err) {
      console.error('Error cancelling:', err);
      alert('Failed to cancel appointment');
    }
  }

  function openDetails(appt) {
    setSelectedAppt(appt);
    setShowCancelForm(false);
    setCancelReason('');
    setShowDetails(true);
  }

  // ─── Loading spinner ────────────────────────────────────────────────────
  function LoadingState() {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#ADB5BD' }}>
        <RefreshCw size={24} style={{ margin: '0 auto 0.75rem', display: 'block', opacity: 0.4 }} />
        Loading…
      </div>
    );
  }

  /*
   * Land on the CRM's Calendar section, not merely on the CRM.
   *
   * ⚠ `tab` is the param CRMPage reads for its section (CRMPage.js:566,
   * `readParam('tab') || loadSectionTab(...) || 'my_day'`), and it is read in
   * a useState INITIALISER — so the param must be written BEFORE CRMPage
   * mounts, which is why writeParams comes first and onNavigate second.
   *
   * ⚠ NOT `cowork.crm.openTab`: that key is only consulted alongside
   * `cowork.crm.openContact` (CRMPage.js:1050) and picks a sub-tab INSIDE an
   * open contact — the Physician Dashboard's deep-link. Using it here would
   * write a key nothing reads and land the user on My Day.
   */
  function openCrmCalendar() {
    try { writeParams({ tab: 'calendar' }); } catch { /* URL unavailable — still navigate */ }
    if (typeof onNavigate === 'function') onNavigate('crm');
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="page-content">

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            ...NEU.statIcon,
            background: 'linear-gradient(145deg, #3CA0AE, #256F7A)',
            color: '#fff', width: 40, height: 40,
          }}>
            <Calendar size={20} />
          </div>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Appointment Scheduling</h1>
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#ADB5BD' }}>Manage patient appointments</p>
            {/*
              P118 PR-3 — Carlos's ruling 2A (25 Oct 2026): consults booked in
              GoHighLevel stay in the CRM Calendar and this page does not show
              them. ⚠ Until now nothing said so. GHL appointments arrive via
              routes/ghl_webhooks.js and land in crm_appointments, which this
              page does not read — so a clinician looking for a booked consult
              found an empty day here with nothing telling them where to look.
            */}
            <p style={{
              margin: '4px 0 0', fontSize: '0.75rem', color: '#6C757D',
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            }}>
              <span>Consults booked in GoHighLevel appear in the CRM Calendar.</span>
              <button
                type="button"
                onClick={openCrmCalendar}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: '#2E8B98', fontSize: '0.75rem', fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                Open CRM Calendar <ExternalLink size={11} />
              </button>
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <PrintButton pageTitle="Appointment Scheduling" />
          {canCreate && (
            <button
              onClick={openCreateForm}
              style={{ ...NEU.btnPrimary, display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '0.875rem' }}
            >
              <Plus size={15} /> New Appointment
            </button>
          )}
        </div>
      </div>

      {/* ── View Toggle + Today ── */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', alignItems: 'center' }}>
        {['month', 'week', 'day'].map(v => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              ...(view === v ? NEU.tabActive : NEU.tab),
              padding: '7px 18px',
              fontSize: '0.8rem',
              textTransform: 'capitalize',
              letterSpacing: '0.02em',
            }}
          >
            {v}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={goToToday}
          style={{ ...NEU.btnGhost, padding: '7px 16px', fontSize: '0.8rem', color: '#2E8B98', fontWeight: 600 }}
        >
          Today
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════
          DAY VIEW
      ══════════════════════════════════════════════════════ */}
      {view === 'day' && (
        <div style={{ ...NEU.cardLg, padding: '1.5rem' }}>
          {/* Day nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <button style={navBtnStyle} onClick={() => navigateDay(-1)}><ChevronLeft size={18} /></button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                style={{ ...neuInputStyle, width: 'auto' }}
              />
              <span style={{ fontWeight: 700, color: '#374151', fontSize: '0.95rem' }}>{formatDate(selectedDate)}</span>
            </div>
            <button style={navBtnStyle} onClick={() => navigateDay(1)}><ChevronRight size={18} /></button>
          </div>

          {loading ? <LoadingState /> : appointments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3.5rem 2rem', color: '#ADB5BD' }}>
              <div style={{ ...NEU.statIcon, margin: '0 auto 1rem', width: 56, height: 56 }}>
                <Calendar size={24} style={{ opacity: 0.35 }} />
              </div>
              <p style={{ margin: 0, fontWeight: 500 }}>No appointments scheduled for this date</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {appointments.map(appt => {
                const sc = STATUS_COLORS[appt.status] || STATUS_COLORS.scheduled;
                const pc = appt.priority ? PRIORITY_COLORS[appt.priority] : null;
                return (
                  <div
                    key={appt.id}
                    onClick={() => openDetails(appt)}
                    style={{
                      ...NEU.cardSm,
                      padding: '1rem 1.25rem',
                      cursor: 'pointer',
                      transition: 'box-shadow 0.15s, transform 0.1s',
                      borderLeft: `3px solid ${sc.border}`,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = NEU.shadow.md; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = NEU.cardSm.boxShadow; e.currentTarget.style.transform = 'none'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                          <Clock size={13} color="#2E8B98" />
                          <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>{formatTime(appt.startTime)} – {formatTime(appt.endTime)}</span>
                          {appt.priority && appt.priority !== 'routine' && pc && (
                            <span style={{ ...NEU.badge, fontSize: '0.65rem', padding: '2px 9px', background: pc.bg, color: pc.text }}>
                              {PRIORITY_LABELS[appt.priority]}
                            </span>
                          )}
                          {appt.appointmentType === 'telehealth' && (
                            <span style={{ ...NEU.badge, display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.65rem', padding: '2px 9px', background: '#eff6ff', color: '#1d4ed8' }}>
                              <Video size={9} /> Telehealth
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.2rem 1rem', fontSize: '0.825rem' }}>
                          <div><span style={{ color: '#ADB5BD' }}>Patient </span><strong>{getPatientName(appt.patientId)}</strong></div>
                          <div><span style={{ color: '#ADB5BD' }}>Type </span><strong>{getTypeLabel(appt.appointmentType)}</strong></div>
                          <div><span style={{ color: '#ADB5BD' }}>Provider </span><strong>{getProviderName(appt.providerId)}</strong></div>
                          {appt.room && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <MapPin size={11} color="#ADB5BD" />
                              <strong>{appt.room}</strong>
                            </div>
                          )}
                          {appt.chiefComplaint && (
                            <div style={{ gridColumn: '1 / -1', color: '#6C757D', fontStyle: 'italic', fontSize: '0.8rem', marginTop: 2 }}>
                              {appt.chiefComplaint}
                            </div>
                          )}
                        </div>
                      </div>
                      <span style={{
                        marginLeft: '1rem', fontSize: '0.7rem', fontWeight: 700,
                        padding: '4px 10px', borderRadius: 999,
                        background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                        {appt.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          WEEK VIEW
      ══════════════════════════════════════════════════════ */}
      {view === 'week' && (
        <div style={{ ...NEU.cardLg, padding: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <button style={navBtnStyle} onClick={() => navigateWeek(-1)}><ChevronLeft size={18} /></button>
            <span style={{ fontWeight: 700, color: '#374151', fontSize: '0.95rem' }}>
              Week of {formatDate(getWeekStart(new Date(selectedDate)).toISOString().split('T')[0])}
            </span>
            <button style={navBtnStyle} onClick={() => navigateWeek(1)}><ChevronRight size={18} /></button>
          </div>

          {loading ? <LoadingState /> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.75rem' }}>
              {getWeekDates().map(date => {
                const isToday    = date === todayStr;
                const isSelected = date === selectedDate;
                const count      = weekAppointments[date] || 0;
                return (
                  <div
                    key={date}
                    onClick={() => { setSelectedDate(date); setView('day'); }}
                    style={{
                      ...(isSelected ? NEU.inset : NEU.cardSm),
                      padding: '1rem 0.5rem',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'box-shadow 0.15s, transform 0.1s',
                      borderTop: isToday ? `2px solid #2E8B98` : '2px solid transparent',
                    }}
                    onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.boxShadow = NEU.shadow.md; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                    onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.boxShadow = NEU.cardSm.boxShadow; e.currentTarget.style.transform = 'none'; } }}
                  >
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: isToday ? '#2E8B98' : '#6C757D', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                      {new Date(date + 'T00:00:00').getDate()}
                    </div>
                    <div style={{
                      fontSize: '1.75rem', fontWeight: 800,
                      color: count > 0 ? '#2E8B98' : '#D1D5DB',
                      lineHeight: 1,
                    }}>
                      {count}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#ADB5BD', marginTop: '0.25rem' }}>
                      appt{count !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MONTH VIEW
      ══════════════════════════════════════════════════════ */}
      {view === 'month' && (
        <div style={{ ...NEU.cardLg, padding: '1.5rem' }}>
          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <button style={navBtnStyle} onClick={() => navigateMonth(-1)}><ChevronLeft size={18} /></button>
            <span style={{ fontWeight: 800, fontSize: '1.15rem', color: '#374151', letterSpacing: '-0.01em' }}>{getMonthLabel()}</span>
            <button style={navBtnStyle} onClick={() => navigateMonth(1)}><ChevronRight size={18} /></button>
          </div>

          {loading ? <LoadingState /> : (
            <>
              {/* Day-of-week header */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '4px' }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} style={{
                    textAlign: 'center', padding: '0.5rem 0',
                    fontWeight: 700, fontSize: '0.7rem',
                    color: day === 'Sun' || day === 'Sat' ? '#ADB5BD' : '#6C757D',
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                {getMonthCalendarDays().map((cell, idx) => {
                  const info       = monthData[cell.date];
                  const isToday    = cell.date === todayStr;
                  const isSelected = cell.date === selectedDate;
                  const hasAppts   = info && info.total > 0;

                  return (
                    <div
                      key={idx}
                      onClick={() => { setSelectedDate(cell.date); setView('day'); }}
                      style={{
                        ...(isSelected ? NEU.inset : { background: '#F8F9FA' }),
                        minHeight: '72px',
                        padding: '0.4rem',
                        cursor: 'pointer',
                        borderRadius: NEU.radiusSm,
                        transition: 'box-shadow 0.15s',
                        opacity: cell.currentMonth ? 1 : 0.38,
                        position: 'relative',
                        boxShadow: isSelected
                          ? NEU.inset.boxShadow
                          : cell.currentMonth
                            ? `2px 2px 5px rgba(186,199,216,0.4), -2px -2px 5px rgba(255,255,255,0.8)`
                            : 'none',
                      }}
                      onMouseEnter={e => { if (!isSelected && cell.currentMonth) e.currentTarget.style.boxShadow = NEU.shadow.sm; }}
                      onMouseLeave={e => { if (!isSelected && cell.currentMonth) e.currentTarget.style.boxShadow = `2px 2px 5px rgba(186,199,216,0.4), -2px -2px 5px rgba(255,255,255,0.8)`; }}
                    >
                      {/* Day number */}
                      <div style={{
                        width: 26, height: 26,
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.8rem',
                        fontWeight: isToday ? 800 : 500,
                        color: isToday ? '#fff' : cell.currentMonth ? '#374151' : '#9CA3AF',
                        background: isToday
                          ? 'linear-gradient(145deg, #3CA0AE, #256F7A)'
                          : 'transparent',
                        boxShadow: isToday
                          ? `2px 2px 5px rgba(0,123,178,0.4), -1px -1px 3px rgba(255,255,255,0.5)`
                          : 'none',
                        marginBottom: 3,
                      }}>
                        {cell.day}
                      </div>

                      {/* Appointment count */}
                      {hasAppts && cell.currentMonth && (
                        <div>
                          <div style={{
                            ...NEU.badge,
                            fontSize: '0.65rem',
                            padding: '2px 7px',
                            background: info.active > 0 ? '#dbeafe' : '#fee2e2',
                            color: info.active > 0 ? '#1e40af' : '#991b1b',
                            display: 'inline-block',
                          }}>
                            {info.active} appt{info.active !== 1 ? 's' : ''}
                          </div>
                          {info.total > info.active && (
                            <div style={{ fontSize: '0.6rem', color: '#ADB5BD', marginTop: 2 }}>
                              +{info.total - info.active} cancelled
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}


      {/* ══════════════════════════════════════════════════════
          CREATE APPOINTMENT MODAL
      ══════════════════════════════════════════════════════ */}
      {showCreateForm && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '1.1rem 1.5rem',
              boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.5)',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151' }}>
                <Calendar size={18} color="#2E8B98" /> New Appointment
              </h2>
              <button
                onClick={() => { setShowCreateForm(false); resetForm(); }}
                style={{ ...NEU.btnGhost, width: 32, height: 32, borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} style={{ padding: '1.25rem 1.5rem' }}>

              {/* Core Information */}
              <SectionLabel>Core Information</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                <div className="form-group">
                  <label className="form-label">Patient *</label>
                  <PatientDropdown
                    patients={patients} value={formData.patientId}
                    onChange={val => updateField('patientId', val)}
                    placeholder="Select a patient" idPrefix="sched"
                  />
                  {formErrors.patientId && <span className="form-error">{formErrors.patientId}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Provider *</label>
                  <select
                    style={formErrors.providerId ? neuInputErrStyle : neuInputStyle}
                    value={formData.providerId}
                    onChange={e => updateField('providerId', e.target.value)}
                  >
                    <option value="">Select a provider</option>
                    {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {formErrors.providerId && <span className="form-error">{formErrors.providerId}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Appointment Type *</label>
                  <select
                    style={formErrors.appointmentType ? neuInputErrStyle : neuInputStyle}
                    value={formData.appointmentType}
                    onChange={e => updateField('appointmentType', e.target.value)}
                  >
                    <option value="">Select type</option>
                    {APPOINTMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  {formErrors.appointmentType && <span className="form-error">{formErrors.appointmentType}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    style={neuInputStyle}
                    value={formData.priority}
                    onChange={e => updateField('priority', e.target.value)}
                  >
                    <option value="routine">Routine</option>
                    <option value="urgent">Urgent</option>
                    <option value="emergent">Emergent</option>
                  </select>
                </div>
              </div>

              {/* Date & Time */}
              <SectionLabel>Date &amp; Time</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date"
                    style={formErrors.appointmentDate ? neuInputErrStyle : neuInputStyle}
                    value={formData.appointmentDate} max={maxDate}
                    onChange={e => updateField('appointmentDate', e.target.value)}
                  />
                  {formErrors.appointmentDate && <span className="form-error">{formErrors.appointmentDate}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Start Time *</label>
                  <input type="time"
                    style={formErrors.startTime ? neuInputErrStyle : neuInputStyle}
                    value={formData.startTime}
                    onChange={e => updateField('startTime', e.target.value)}
                  />
                  {formErrors.startTime && <span className="form-error">{formErrors.startTime}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Duration (min)</label>
                  <select style={neuInputStyle} value={formData.durationMinutes} onChange={e => updateField('durationMinutes', e.target.value)}>
                    {[15, 20, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">End Time *</label>
                  <input type="time"
                    style={formErrors.endTime ? neuInputErrStyle : neuInputStyle}
                    value={formData.endTime}
                    onChange={e => updateField('endTime', e.target.value)}
                  />
                  {formErrors.endTime && <span className="form-error">{formErrors.endTime}</span>}
                </div>
              </div>

              {/* Clinical */}
              <SectionLabel>Clinical</SectionLabel>
              <div style={{ marginBottom: '1.25rem' }}>
                <div className="form-group">
                  <label className="form-label">Chief Complaint *</label>
                  <textarea
                    style={formErrors.chiefComplaint ? { ...neuInputErrStyle, resize: 'vertical' } : { ...neuInputStyle, resize: 'vertical' }}
                    value={formData.chiefComplaint}
                    onChange={e => updateField('chiefComplaint', e.target.value)}
                    placeholder="Primary reason for this visit"
                    rows={2}
                  />
                  {formErrors.chiefComplaint && <span className="form-error">{formErrors.chiefComplaint}</span>}
                </div>
                <div className="form-group" style={{ marginTop: '0.75rem' }}>
                  <label className="form-label">Additional Notes / Instructions</label>
                  <textarea
                    style={{ ...neuInputStyle, resize: 'vertical' }}
                    value={formData.notes}
                    onChange={e => updateField('notes', e.target.value)}
                    placeholder="Preparation instructions, special considerations, etc."
                    rows={2}
                  />
                </div>
              </div>

              {/* Location */}
              <SectionLabel>Location</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                <div className="form-group">
                  <label className="form-label">Room / Location</label>
                  <input type="text" style={neuInputStyle}
                    value={formData.room}
                    onChange={e => updateField('room', e.target.value)}
                    placeholder="e.g., Room 101, Exam B"
                  />
                </div>
                {formData.appointmentType === 'telehealth' && (
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Video size={12} /> Telehealth Link *
                    </label>
                    <input type="url"
                      style={formErrors.telehealthLink ? neuInputErrStyle : neuInputStyle}
                      value={formData.telehealthLink}
                      onChange={e => updateField('telehealthLink', e.target.value)}
                      placeholder="https://meet.example.com/..."
                    />
                    {formErrors.telehealthLink && <span className="form-error">{formErrors.telehealthLink}</span>}
                  </div>
                )}
              </div>

              {/* Administrative */}
              <SectionLabel>Administrative</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                <div className="form-group">
                  <label className="form-label">Referral Source</label>
                  <input type="text" style={neuInputStyle}
                    value={formData.referralSource}
                    onChange={e => updateField('referralSource', e.target.value)}
                    placeholder="e.g., Self-referral, Dr. Smith"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Insurance Pre-Auth Number</label>
                  <input type="text" style={neuInputStyle}
                    value={formData.insuranceAuthNumber}
                    onChange={e => updateField('insuranceAuthNumber', e.target.value)}
                    placeholder="Authorization #"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Copay Amount ($)</label>
                  <input type="number" min="0" step="0.01"
                    style={formErrors.copayAmount ? neuInputErrStyle : neuInputStyle}
                    value={formData.copayAmount}
                    onChange={e => updateField('copayAmount', e.target.value)}
                    placeholder="0.00"
                  />
                  {formErrors.copayAmount && <span className="form-error">{formErrors.copayAmount}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <RefreshCw size={12} /> Recurrence
                  </label>
                  <select style={neuInputStyle} value={formData.recurrence} onChange={e => updateField('recurrence', e.target.value)}>
                    {RECURRENCE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Reminder checkbox */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  cursor: 'pointer', fontSize: '0.875rem', color: '#374151',
                  padding: '8px 12px', borderRadius: NEU.radiusSm,
                  boxShadow: NEU.shadow.insetSm, background: '#F8F9FA',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.reminderSent}
                    onChange={e => updateField('reminderSent', e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <Bell size={13} color="#2E8B98" /> Reminder already sent to patient
                </label>
              </div>

              {/* Form actions */}
              <div style={{
                display: 'flex', gap: '0.75rem',
                paddingTop: '1rem',
                boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.5)',
              }}>
                <button type="submit" disabled={saving}
                  style={{ ...NEU.btnPrimary, flex: 1, padding: '10px', fontSize: '0.875rem', opacity: saving ? 0.6 : 1 }}
                >
                  {saving ? 'Saving…' : 'Create Appointment'}
                </button>
                <button type="button"
                  onClick={() => { setShowCreateForm(false); resetForm(); }}
                  style={{ ...NEU.btnGhost, flex: 1, padding: '10px', fontSize: '0.875rem' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* ══════════════════════════════════════════════════════
          APPOINTMENT DETAILS MODAL
      ══════════════════════════════════════════════════════ */}
      {showDetails && selectedAppt && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '1.1rem 1.5rem',
              boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.5)',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#374151' }}>
                Appointment Details
              </h2>
              <button
                onClick={() => { setShowDetails(false); setShowCancelForm(false); setCancelReason(''); }}
                style={{ ...NEU.btnGhost, width: 32, height: 32, borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '1.25rem 1.5rem' }}>
              {/* Status + priority badges */}
              {(() => {
                const sc = STATUS_COLORS[selectedAppt.status] || STATUS_COLORS.scheduled;
                return (
                  <div style={{ marginBottom: '1.25rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{
                      ...NEU.badge, fontSize: '0.75rem', padding: '4px 14px',
                      background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                    }}>
                      {selectedAppt.status?.replace(/_/g, ' ')}
                    </span>
                    {selectedAppt.priority && selectedAppt.priority !== 'routine' && (
                      <span style={{
                        ...NEU.badge, fontSize: '0.75rem', padding: '4px 14px',
                        background: PRIORITY_COLORS[selectedAppt.priority]?.bg,
                        color: PRIORITY_COLORS[selectedAppt.priority]?.text,
                      }}>
                        {PRIORITY_LABELS[selectedAppt.priority]}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Detail grid */}
              <div style={{
                ...NEU.inset,
                padding: '1rem',
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: '0.75rem 1.5rem',
                marginBottom: '1.25rem',
                fontSize: '0.875rem',
              }}>
                <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Patient</div><div style={{ fontWeight: 700 }}>{getPatientName(selectedAppt.patientId)}</div></div>
                <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Provider</div><div style={{ fontWeight: 700 }}>{getProviderName(selectedAppt.providerId)}</div></div>
                <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Date &amp; Time</div><div style={{ fontWeight: 700 }}>{formatDate(selectedAppt.appointmentDate)} &nbsp; {formatTime(selectedAppt.startTime)} – {formatTime(selectedAppt.endTime)}</div></div>
                <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Type</div><div style={{ fontWeight: 600 }}>{getTypeLabel(selectedAppt.appointmentType)}</div></div>
                {selectedAppt.room && <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Room</div><div style={{ fontWeight: 600 }}>{selectedAppt.room}</div></div>}
                {selectedAppt.durationMinutes && <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Duration</div><div style={{ fontWeight: 600 }}>{selectedAppt.durationMinutes} min</div></div>}
                {selectedAppt.referralSource && <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Referral Source</div><div>{selectedAppt.referralSource}</div></div>}
                {selectedAppt.insuranceAuthNumber && <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Insurance Auth #</div><div>{selectedAppt.insuranceAuthNumber}</div></div>}
                {selectedAppt.copayAmount && <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Copay</div><div>${parseFloat(selectedAppt.copayAmount).toFixed(2)}</div></div>}
                {selectedAppt.recurrence && selectedAppt.recurrence !== '' && (
                  <div><div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Recurrence</div><div>{RECURRENCE_OPTIONS.find(r => r.value === selectedAppt.recurrence)?.label || selectedAppt.recurrence}</div></div>
                )}
              </div>

              {/* Chief complaint */}
              {selectedAppt.chiefComplaint && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Chief Complaint</div>
                  <p style={{ margin: 0, padding: '0.75rem', ...NEU.inset, fontSize: '0.875rem' }}>{selectedAppt.chiefComplaint}</p>
                </div>
              )}

              {selectedAppt.reason && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Reason</div>
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>{selectedAppt.reason}</p>
                </div>
              )}

              {selectedAppt.notes && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Notes</div>
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{selectedAppt.notes}</p>
                </div>
              )}

              {selectedAppt.telehealthLink && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.7rem', color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Video size={11} /> Telehealth Link
                  </div>
                  <a href={selectedAppt.telehealthLink} target="_blank" rel="noopener noreferrer" style={{ color: '#2E8B98', wordBreak: 'break-all', fontSize: '0.875rem' }}>
                    {selectedAppt.telehealthLink}
                  </a>
                </div>
              )}

              {/* Status update buttons */}
              {canUpdate && (
                <div style={{ paddingTop: '1rem', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.5)', marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.7rem', fontWeight: 700, marginBottom: '0.75rem', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Update Status</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    {[
                      { status: 'confirmed',   label: 'Confirm',     icon: <Check size={13} />,         sc: STATUS_COLORS.confirmed },
                      { status: 'checked_in',  label: 'Check In',    icon: <User size={13} />,          sc: STATUS_COLORS.checked_in },
                      { status: 'in_progress', label: 'In Progress', icon: <Clock size={13} />,         sc: STATUS_COLORS.in_progress },
                      { status: 'completed',   label: 'Complete',    icon: <Check size={13} />,         sc: STATUS_COLORS.completed },
                      { status: 'no_show',     label: 'No Show',     icon: <AlertTriangle size={13} />, sc: STATUS_COLORS.no_show },
                    ].filter(btn => btn.status !== selectedAppt.status).map(btn => (
                      <button
                        key={btn.status}
                        onClick={() => handleStatusUpdate(btn.status)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          padding: '7px', borderRadius: NEU.radiusSm, fontSize: '0.78rem', fontWeight: 700,
                          border: `1px solid ${btn.sc.border}`,
                          background: btn.sc.bg, color: btn.sc.text,
                          cursor: 'pointer',
                          boxShadow: `2px 2px 5px rgba(186,199,216,0.4), -1px -1px 3px rgba(255,255,255,0.8)`,
                          transition: 'box-shadow 0.12s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = `3px 3px 7px rgba(186,199,216,0.5), -2px -2px 5px rgba(255,255,255,0.9)`}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = `2px 2px 5px rgba(186,199,216,0.4), -1px -1px 3px rgba(255,255,255,0.8)`}
                      >
                        {btn.icon} {btn.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Cancel section */}
              {canDelete && selectedAppt.status !== 'cancelled' && (
                <div style={{ paddingTop: '1rem', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.5)' }}>
                  {!showCancelForm ? (
                    <button
                      onClick={() => setShowCancelForm(true)}
                      style={{
                        ...NEU.btnGhost,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        width: '100%', padding: '8px', fontSize: '0.875rem', fontWeight: 700,
                        color: '#991b1b',
                      }}
                    >
                      <X size={14} /> Cancel Appointment
                    </button>
                  ) : (
                    <div>
                      <div className="form-group">
                        <label className="form-label">Cancellation Reason *</label>
                        <textarea
                          style={{ ...neuInputStyle, resize: 'vertical' }}
                          value={cancelReason}
                          onChange={e => setCancelReason(e.target.value)}
                          placeholder="Enter reason for cancellation"
                          rows={2}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <button
                          onClick={handleCancel}
                          style={{ ...NEU.btnDanger, flex: 1, padding: '8px', fontSize: '0.875rem' }}
                        >
                          Confirm Cancellation
                        </button>
                        <button
                          onClick={() => { setShowCancelForm(false); setCancelReason(''); }}
                          style={{ ...NEU.btnGhost, flex: 1, padding: '8px', fontSize: '0.875rem' }}
                        >
                          Keep Appointment
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Create encounter */}
              {(selectedAppt.status === 'checked_in' || selectedAppt.status === 'in_progress') && (
                <div style={{ paddingTop: '1rem', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.5)', marginTop: '1rem' }}>
                  <button
                    onClick={async () => {
                      try {
                        const result = await window.zignagenixAPI.appointmentToEncounter(selectedAppt.id);
                        if (result?.success) {
                          alert('Encounter created successfully from appointment');
                          setShowDetails(false);
                          loadAppointments();
                        } else {
                          alert('Failed to create encounter');
                        }
                      } catch (err) {
                        alert('Error creating encounter: ' + err.message);
                      }
                    }}
                    style={{ ...NEU.btnPrimary, width: '100%', padding: '10px', fontSize: '0.875rem' }}
                  >
                    Create Encounter
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
